import type { CmmdWorkIntake } from "@cmmd-forge/contracts";

/**
 * Warning text that means the SESSION is the problem, so signing in again fixes
 * it. These drive a full product login, which is a destructive enough response
 * that the match has to be about authentication specifically.
 *
 * Deliberately absent: "forbidden". A 403 means authenticated but not permitted,
 * and CMMD returns one for causes that have nothing to do with the session, such
 * as an app switched off for the organization ("The campaigns app has been
 * disabled for your organization"). Treating that as an expired session throws
 * the user out to a login that cannot fix it, and they land back in the same
 * state. "Unauthorized" stays: 401 is the status that really does mean the
 * credential was not accepted.
 */
const CMMD_AUTH_FAILURE_MESSAGE_PATTERNS: ReadonlyArray<RegExp> = [
  /\bCMMD_AUTH_(REQUIRED|EXPIRED)\b/i,
  /\bcmmd\b.*\bauth/i,
  /\bauthentication\b.*\b(required|expired|invalid)\b/i,
  /\bsign in again\b/i,
  /\blog in again\b/i,
  /\bunauthorized\b/i,
  /\btoken\b.*\b(expired|invalid)\b/i,
];

/**
 * Whether a snapshot that came back disconnected did so because the session is
 * no longer good. A connected snapshot is never an auth failure, however many
 * individual legs degraded.
 */
export function isAuthFailureIntake(intake: CmmdWorkIntake): boolean {
  if (intake.connected) return false;
  return intake.warnings.some((warning) =>
    CMMD_AUTH_FAILURE_MESSAGE_PATTERNS.some((pattern) => pattern.test(warning)),
  );
}
